import { useState, useRef, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    LinearProgress,
    Divider,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faFolderOpen,
    faFilePdf,
    faTimes,
    faCheckCircle,
    faExclamationTriangle,
    faUpload
} from '@fortawesome/free-solid-svg-icons'
import documentsRoutes from '../routes/documents.routes'
import { ConfirmarDialog } from './Confirmar.dialog'
import AlertDialog from './AlertDialog'

const UPLOAD_STATES = {
    IDLE: 'idle',
    PREVIEW: 'preview',
    VALIDATING: 'validating',
    UPLOADING: 'uploading',
    DONE: 'done',
    ERROR: 'error'
}

export const SpmUploadDialog = ({ open, handleClose, defaultSpm = '', defaultStep = '', mode = 'folder' }) => {
    const folderInputRef = useRef(null)
    const fileInputRef = useRef(null)

    const [uploadState, setUploadState] = useState(UPLOAD_STATES.IDLE)
    const [spmToUpload, setSpmToUpload] = useState('')
    const [spmType, setSpmType] = useState('inspeccion')
    const [spmValid, setSpmValid] = useState(false)
    const [spmValidationLoading, setSpmValidationLoading] = useState(false)

    const [fileList, setFileList] = useState([])
    const [pathList, setPathList] = useState([])
    const [stepsMap, setStepsMap] = useState({})
    const [targetStepName, setTargetStepName] = useState('General')

    const [currentSasData, setCurrentSasData] = useState(null)
    const [uploadedCount, setUploadedCount] = useState(0)
    const [totalFiles, setTotalFiles] = useState(0)
    const [currentFileName, setCurrentFileName] = useState('')
    const [fileProgress, setFileProgress] = useState(0)
    const [overallProgress, setOverallProgress] = useState(0)

    const [errorMessage, setErrorMessage] = useState('')
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [alertOpen, setAlertOpen] = useState(false)
    const [confirmMessage, setConfirmMessage] = useState('')
    const [confirmOnConfirm, setConfirmOnConfirm] = useState(null)
    const [alertMessage, setAlertMessage] = useState('')
    const [alertTitle, setAlertTitle] = useState('Atención')
    const xhrRef = useRef(null)

    // Inicializar cuando se abre el diálogo con valores por defecto
    useEffect(() => {
        if (open) {
            setSpmToUpload(defaultSpm || '')
            setTargetStepName(defaultStep || 'General')
            if (defaultSpm) {
                setSpmValid(true)
                const checkSpm = async () => {
                    try {
                        const res = await documentsRoutes.validateSpm(defaultSpm)
                        if (res.data && res.data.isValid) {
                            setSpmValid(true)
                            setSpmType(res.data.spmType || 'inspeccion')
                        }
                    } catch (err) {
                        console.error('Error al validar el SPM por defecto:', err)
                    }
                }
                checkSpm()
            }
        }
    }, [open, defaultSpm, defaultStep])

    const triggerConfirm = (message, onConfirm) => {
        setConfirmMessage(message)
        setConfirmOnConfirm(() => () => {
            onConfirm()
            setConfirmOpen(false)
        })
        setConfirmOpen(true)
    }

    const triggerAlert = (message, title = 'Atención') => {
        setAlertTitle(title)
        setAlertMessage(message)
        setConfirmOpen(false)
        setAlertOpen(true)
    }

    const resetState = () => {
        setUploadState(UPLOAD_STATES.IDLE)
        setSpmToUpload(defaultSpm || '')
        setSpmType('inspeccion')
        setSpmValid(!!defaultSpm)
        setFileList([])
        setPathList([])
        setStepsMap({})
        setCurrentSasData(null)
        setUploadedCount(0)
        setTotalFiles(0)
        setCurrentFileName('')
        setFileProgress(0)
        setOverallProgress(0)
        setErrorMessage('')
        setTargetStepName(defaultStep || 'General')
    }

    const handleCloseDialog = () => {
        if (uploadState === UPLOAD_STATES.UPLOADING && xhrRef.current) {
            xhrRef.current.abort()
            xhrRef.current = null
        }
        resetState()
        handleClose()
    }

    const handleSelectFolder = () => {
        if (folderInputRef.current) {
            folderInputRef.current.click()
        }
    }

    const handleSelectFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const handleFolderChange = async (e) => {
        const files = Array.from(e.target.files)
        if (files.length === 0) return

        // Validar tamaño de archivos (límite de 5MB por archivo)
        const maxSizeBytes = 5 * 1024 * 1024
        const oversizedFiles = files.filter((f) => f.size > maxSizeBytes)
        if (oversizedFiles.length > 0) {
            const fileListStr = oversizedFiles
                .slice(0, 5)
                .map((f) => `- ${f.name} (${(f.size / (1024 * 1024)).toFixed(2)} MB)`)
                .join('\n')
            const remainingCount = oversizedFiles.length - 5
            const suffix =
                remainingCount > 0 ? `\n...y ${remainingCount} archivo(s) más` : ''

            triggerAlert(
                `No se puede cargar la carpeta. Los siguientes archivos superan el límite de 5MB por archivo:\n${fileListStr}${suffix}`
            )
            e.target.value = ''
            return
        }

        const paths = files.map((f) => f.webkitRelativePath || f.name)
        const firstPath = paths[0].replace(/\\/g, '/')
        const parts = firstPath.split('/')
        const rootFolder = parts[0]

        const isSpmRoot = /^SPM\d+/i.test(rootFolder)
        let detectedSpm = ''
        if (isSpmRoot) {
            detectedSpm = rootFolder.toUpperCase()
        } else {
            for (const path of paths) {
                const clean = path.replace(/\\/g, '/')
                const pParts = clean.split('/')
                for (let i = 0; i < pParts.length - 1; i++) {
                    if (/^SPM\d+/i.test(pParts[i])) {
                        detectedSpm = pParts[i].toUpperCase()
                        break
                    }
                }
                if (detectedSpm) break
            }
        }

        const grouped = {}
        files.forEach((file, idx) => {
            const relPath = paths[idx].replace(/\\/g, '/')
            const pParts = relPath.split('/')
            let stepName = 'General'
            let filename = file.name

            if (pParts.length >= 3) {
                stepName = pParts[pParts.length - 2]
                filename = pParts[pParts.length - 1]
            } else if (pParts.length === 2) {
                stepName = 'General'
                filename = pParts[1]
            }

            if (!grouped[stepName]) {
                grouped[stepName] = []
            }
            grouped[stepName].push({
                filename,
                size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
                file,
                path: paths[idx]
            })
        })

        setFileList(files)
        setPathList(paths)
        setStepsMap(grouped)
        if (detectedSpm) {
            setSpmToUpload(detectedSpm)
            setSpmValid(false)
            if (defaultSpm && detectedSpm === defaultSpm.toUpperCase()) {
                setSpmValid(true)
            }
        }
        setUploadState(UPLOAD_STATES.PREVIEW)
        e.target.value = ''
    }

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files)
        if (files.length === 0) return

        // Validar tamaño de archivos
        const maxSizeBytes = 5 * 1024 * 1024
        const oversizedFiles = files.filter((f) => f.size > maxSizeBytes)
        if (oversizedFiles.length > 0) {
            const fileListStr = oversizedFiles
                .slice(0, 5)
                .map((f) => `- ${f.name} (${(f.size / (1024 * 1024)).toFixed(2)} MB)`)
                .join('\n')
            const remainingCount = oversizedFiles.length - 5
            const suffix =
                remainingCount > 0 ? `\n...y ${remainingCount} archivo(s) más` : ''

            triggerAlert(
                `No se puede cargar el/los archivo(s). Los siguientes archivos superan el límite de 5MB por archivo:\n${fileListStr}${suffix}`
            )
            e.target.value = ''
            return
        }

        const currentSpm = spmToUpload || defaultSpm || 'SPM'
        const currentStep = targetStepName || 'General'

        const paths = files.map((f) => `${currentSpm}/${currentStep}/${f.name}`)

        const grouped = {}
        grouped[currentStep] = files.map((file, idx) => ({
            filename: file.name,
            size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
            file,
            path: paths[idx]
        }))

        setFileList(files)
        setPathList(paths)
        setStepsMap(grouped)
        setUploadState(UPLOAD_STATES.PREVIEW)
        e.target.value = ''
    }

    const handleValidateSpm = async () => {
        if (!spmToUpload.trim()) {
            triggerAlert('Debe ingresar un código SPM.')
            return
        }

        setSpmValidationLoading(true)
        try {
            const res = await documentsRoutes.validateSpm(spmToUpload.trim())
            if (res.data && res.data.isValid) {
                setSpmValid(true)
                setSpmType(res.data.spmType || 'inspeccion')
            } else {
                setSpmValid(false)
                triggerAlert(
                    `El SPM "${spmToUpload.trim()}" no coincide con ninguna máquina activa en el sistema.`,
                    'SPM no válido'
                )
            }
        } catch (error) {
            setSpmValid(false)
            triggerAlert(
                `Error validando SPM: ${error.response?.data?.message || error.message}`,
                'Error'
            )
        } finally {
            setSpmValidationLoading(false)
        }
    }

    const handleStartUpload = () => {
        triggerConfirm(
            `¿Está seguro de subir ${fileList.length} archivo(s) al SPM "${spmToUpload}"?`,
            async () => {
                await executeUpload()
            }
        )
    }

    const uploadSingleFile = (sasUrl, file, contentType) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()
            xhrRef.current = xhr
            xhr.open('PUT', sasUrl, true)
            xhr.setRequestHeader('Content-Type', contentType)
            xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob')

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const pct = Math.round((event.loaded / event.total) * 100)
                    setFileProgress(pct)
                }
            }

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve()
                } else {
                    reject(new Error(`HTTP ${xhr.status}: Azure upload failed`))
                }
            }

            xhr.onerror = () => {
                xhrRef.current = null
                reject(new Error('Network error during Azure upload'))
            }

            xhr.onloadend = () => {
                xhrRef.current = null
            }

            xhr.send(file)
        })
    }

    const executeUpload = async () => {
        setUploadState(UPLOAD_STATES.UPLOADING)
        setErrorMessage('')
        setUploadedCount(0)
        setTotalFiles(fileList.length)
        setFileProgress(0)
        setOverallProgress(0)
        setCurrentSasData(null)

        try {
            let finalPaths = []
            if (mode === 'file') {
                const step = (targetStepName || 'General').trim().toUpperCase()
                finalPaths = fileList.map((file) => {
                    return `${spmToUpload.trim().toUpperCase()}/${step}/${file.name}`
                })
            } else {
                finalPaths = pathList.map((p) => {
                    const clean = p.replace(/\\/g, '/')
                    const parts = clean.split('/')
                    parts[0] = spmToUpload.trim().toUpperCase()
                    return parts.join('/')
                })
            }

            const prepRes = await documentsRoutes.prepareUpload(
                finalPaths,
                spmToUpload.trim()
            )
            const { uploads } = prepRes.data

            setCurrentSasData({ spm: prepRes.data.spm, uploads })

            const completedUploads = []
            let failed = false

            for (let i = 0; i < uploads.length; i++) {
                const upload = uploads[i]
                const fileEntry = fileList.find(
                    (_, idx) => finalPaths[idx] === upload.path
                )

                if (!fileEntry) {
                    console.warn(`File not found for path: ${upload.path}`)
                    continue
                }

                setCurrentFileName(upload.filename)
                setFileProgress(0)

                const contentType = fileEntry.type || 'application/octet-stream'
                try {
                    await uploadSingleFile(upload.sasUrl, fileEntry, contentType)
                    completedUploads.push({
                        blobPath: upload.blobPath,
                        filename: upload.filename,
                        stepName: upload.stepName
                    })
                    setUploadedCount((prev) => prev + 1)
                    setOverallProgress(Math.round(((i + 1) / uploads.length) * 100))
                } catch (uploadError) {
                    console.error(`Upload failed for ${upload.filename}:`, uploadError)
                    setErrorMessage(
                        `Error al subir "${upload.filename}": ${uploadError.message}`
                    )
                    setUploadState(UPLOAD_STATES.ERROR)
                    failed = true
                    break
                }
            }

            if (failed || completedUploads.length === 0) {
                if (!failed) {
                    setErrorMessage('No se completó ninguna subida.')
                    setUploadState(UPLOAD_STATES.ERROR)
                }
                return
            }

            setFileProgress(100)
            setOverallProgress(100)

            await documentsRoutes.confirmUpload(spmToUpload.trim(), completedUploads)

            setUploadState(UPLOAD_STATES.DONE)
        } catch (error) {
            console.error('Upload flow error:', error)
            setErrorMessage(
                `Error en el flujo de subida: ${error.response?.data?.message || error.message}`
            )
            setUploadState(UPLOAD_STATES.ERROR)
        }
    }

    const renderPreview = () => {
        const stepEntries = Object.entries(stepsMap)
        const totalFilesCount = fileList.length

        return (
            <Box>
                <Box mb={2} display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Resumen de archivos a subir
                    </Typography>
                    <Chip
                        label={`${totalFilesCount} archivo(s)`}
                        size="small"
                        color="primary"
                    />
                </Box>

                {mode === 'file' ? (
                    <Box mb={2}>
                        <Typography variant="body2" fontWeight={700} color="primary" mb={0.5}>
                            Archivos seleccionados ({totalFilesCount})
                        </Typography>
                        <Box pl={2} mb={2} style={{ maxHeight: 150, overflowY: 'auto' }}>
                            {fileList.map((file) => (
                                <Box key={file.name} display="flex" alignItems="center" gap={1} mb={0.5}>
                                    <FontAwesomeIcon
                                        icon={faFilePdf}
                                        style={{ color: '#E53935', fontSize: 12 }}
                                    />
                                    <Typography variant="caption" noWrap style={{ maxWidth: 350 }}>
                                        {file.name}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                Guía de destino:
                            </Typography>
                            <input
                                type="text"
                                value={targetStepName}
                                onChange={(e) => setTargetStepName(e.target.value)}
                                placeholder="Ej: PASO 1"
                                style={{
                                    border: '1px solid #ccc',
                                    borderRadius: 4,
                                    padding: '6px 12px',
                                    width: 180,
                                    fontSize: 14
                                }}
                            />
                        </Box>
                    </Box>
                ) : (
                    stepEntries.map(([stepName, files]) => (
                        <Box key={stepName} mb={2}>
                            <Typography
                                variant="body2"
                                fontWeight={700}
                                color="primary"
                                mb={0.5}
                            >
                                {stepName} ({files.length} archivo(s))
                            </Typography>
                            <Box pl={2}>
                                {files.map((f) => (
                                    <Box
                                        key={f.filename}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        mb={0.5}
                                    >
                                        <FontAwesomeIcon
                                            icon={faFilePdf}
                                            style={{ color: '#E53935', fontSize: 12 }}
                                        />
                                        <Typography
                                            variant="caption"
                                            noWrap
                                            style={{ maxWidth: 350 }}
                                        >
                                            {f.filename}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {f.size}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))
                )}

                <Divider style={{ margin: '16px 0' }} />

                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                        Código SPM:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                        <input
                            type="text"
                            value={spmToUpload}
                            onChange={(e) => {
                                setSpmToUpload(e.target.value)
                                setSpmValid(false)
                            }}
                            placeholder="SPM000123"
                            style={{
                                border: '1px solid #ccc',
                                borderRadius: 4,
                                padding: '6px 12px',
                                width: 180,
                                fontSize: 14
                            }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleValidateSpm}
                            disabled={spmValidationLoading || !spmToUpload.trim()}
                            startIcon={
                                spmValidationLoading ? <CircularProgress size={14} /> : null
                            }
                        >
                            Validar
                        </Button>
                        {spmValid && (
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                style={{ color: '#4CAF50' }}
                            />
                        )}
                    </Box>
                </Box>
                {spmValid && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        mb={2}
                        display="block"
                    >
                        SPM válido (tipo: {spmType})
                    </Typography>
                )}
            </Box>
        )
    }

    const renderUploading = () => {
        return (
            <Box>
                <Box mb={2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Subiendo archivos...
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Archivo {uploadedCount} de {totalFiles}
                        {currentFileName && `: ${currentFileName}`}
                    </Typography>
                </Box>

                <Box mb={1.5}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" fontWeight={600}>
                            Progreso actual
                        </Typography>
                        <Typography variant="caption" fontWeight={800} color="primary">
                            {fileProgress}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={fileProgress}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#E0E2F3',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                backgroundColor: '#3F51B5'
                            }
                        }}
                    />
                </Box>

                <Box mb={1.5}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" fontWeight={600}>
                            Progreso total
                        </Typography>
                        <Typography variant="caption" fontWeight={800} color="secondary">
                            {overallProgress}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={overallProgress}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#E8E0F3',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                backgroundColor: '#7B1FA2'
                            }
                        }}
                    />
                </Box>

                {currentSasData && (
                    <Typography variant="caption" color="textSecondary">
                        Token SAS válido por {currentSasData.uploads.length} archivo(s)
                    </Typography>
                )}
            </Box>
        )
    }

    const renderDone = () => {
        return (
            <Box textAlign="center" py={4}>
                <FontAwesomeIcon
                    icon={faCheckCircle}
                    style={{ fontSize: 48, color: '#4CAF50', marginBottom: 16 }}
                />
                <Typography variant="h6" fontWeight={600} mb={1}>
                    ¡Subida completada!
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {uploadedCount} archivo(s) subido(s) exitosamente al SPM {spmToUpload}.
                </Typography>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    mt={1}
                >
                    Los archivos fueron subidos.
                </Typography>
            </Box>
        )
    }

    const renderError = () => {
        return (
            <Box textAlign="center" py={4}>
                <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    style={{ fontSize: 48, color: '#E53935', marginBottom: 16 }}
                />
                <Typography variant="h6" fontWeight={600} mb={1}>
                    Error en la subida
                </Typography>
                <Alert severity="error" style={{ textAlign: 'left' }}>
                    {errorMessage}
                </Alert>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    mt={2}
                >
                    Los archivos que ya fueron subidos a Azure pueden quedar huérfanos. El
                    token SAS expiró o hubo un error de red.
                </Typography>
            </Box>
        )
    }

    const renderContent = () => {
        switch (uploadState) {
        case UPLOAD_STATES.PREVIEW:
            return renderPreview()
        case UPLOAD_STATES.UPLOADING:
            return renderUploading()
        case UPLOAD_STATES.DONE:
            return renderDone()
        case UPLOAD_STATES.ERROR:
            return renderError()
        default:
            return null
        }
    }

    const showActions = () => {
        if (uploadState === UPLOAD_STATES.IDLE) {
            return (
                <>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon={mode === 'folder' ? faFolderOpen : faFilePdf} />}
                        onClick={mode === 'folder' ? handleSelectFolder : handleSelectFile}
                    >
                        {mode === 'folder' ? 'Seleccionar Carpeta' : 'Seleccionar Archivos'}
                    </Button>
                </>
            )
        }
        if (uploadState === UPLOAD_STATES.PREVIEW) {
            return (
                <>
                    <Button
                        onClick={() => {
                            setUploadState(UPLOAD_STATES.IDLE)
                            setFileList([])
                            setPathList([])
                            setStepsMap({})
                            setSpmToUpload(defaultSpm || '')
                            setSpmValid(!!defaultSpm)
                        }}
                    >
                        Volver
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!spmValid}
                        startIcon={<FontAwesomeIcon icon={faUpload} />}
                        onClick={handleStartUpload}
                    >
                        Subir {fileList.length} archivo(s)
                    </Button>
                </>
            )
        }
        if (
            uploadState === UPLOAD_STATES.DONE ||
            uploadState === UPLOAD_STATES.ERROR
        ) {
            return (
                <>
                    <Button onClick={handleCloseDialog}>Cerrar</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setUploadState(UPLOAD_STATES.IDLE)
                            setFileList([])
                            setPathList([])
                            setStepsMap({})
                            setSpmToUpload(defaultSpm || '')
                            setSpmValid(!!defaultSpm)
                            setErrorMessage('')
                        }}
                    >
                        Subir más archivos
                    </Button>
                </>
            )
        }
        return null
    }

    return (
        <>
            <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle
                    style={{
                        backgroundColor: '#F4F4F4',
                        padding: '12px 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <FontAwesomeIcon
                            icon={mode === 'folder' ? faFolderOpen : faFilePdf}
                            style={{ marginRight: 10, color: '#3f51b5' }}
                        />
                        <Typography
                            variant="h6"
                            component="span"
                            style={{ fontWeight: 600 }}
                        >
                            {mode === 'folder'
                                ? 'Subir Carpeta Técnica'
                                : 'Subir Archivo(s) Técnico(s)'}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleCloseDialog}>
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                </DialogTitle>

                <input
                    type="file"
                    ref={folderInputRef}
                    style={{ display: 'none' }}
                    webkitdirectory=""
                    multiple
                    onChange={handleFolderChange}
                />

                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    multiple
                    accept=".pdf"
                    onChange={handleFileChange}
                />

                <DialogContent dividers style={{ minHeight: 300 }}>
                    {uploadState === UPLOAD_STATES.IDLE && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            height={300}
                            border="2px dashed #ccc"
                            borderRadius={2}
                            cursor="pointer"
                            onClick={mode === 'folder' ? handleSelectFolder : handleSelectFile}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#3f51b5'
                                e.currentTarget.style.backgroundColor = '#f5f5ff'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#ccc'
                                e.currentTarget.style.backgroundColor = 'transparent'
                            }}
                        >
                            <FontAwesomeIcon
                                icon={mode === 'folder' ? faFolderOpen : faFilePdf}
                                style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }}
                            />
                            <Typography
                                variant="body1"
                                fontWeight={600}
                                color="textSecondary"
                                align="center"
                                px={2}
                            >
                                {mode === 'folder'
                                    ? 'Haga clic o arrastre una carpeta con estructura SPM/PASO/Archivos'
                                    : 'Haga clic o arrastre archivos PDF de documentación técnica'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" mt={1}>
                                Límite: 5 MB por archivo
                            </Typography>
                        </Box>
                    )}

                    {uploadState !== UPLOAD_STATES.IDLE && renderContent()}
                </DialogContent>

                <DialogActions style={{ padding: '12px 24px' }}>
                    {showActions()}
                </DialogActions>
            </Dialog>

            <ConfirmarDialog
                open={confirmOpen}
                handleClose={() => setConfirmOpen(false)}
                message={confirmMessage}
                select={confirmOnConfirm}
            />

            <AlertDialog
                open={alertOpen}
                title={alertTitle}
                message={alertMessage}
                onClose={() => setAlertOpen(false)}
                actions={[
                    {
                        text: 'Aceptar',
                        handler: () => setAlertOpen(false),
                        props: { variant: 'contained', color: 'primary', style: { borderRadius: 8 } }
                    }
                ]}
            />
        </>
    )
}

export default SpmUploadDialog
